import { FC, ReactNode, useId } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Dashboard } from "@/models/dashboard/Dashboard";
import { Building2, ChevronDown, Plus, Share2, User } from "lucide-react";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import {
  getDashboardIdentity,
  groupDashboardsByAudience,
  parentTenantDisplayName,
} from "../lib/dashboardIdentity";
import {
  DashboardIdentityPill,
  UnpublishedChangesChip,
} from "./DashboardIdentityPill";

interface DashboardSelectorProps {
  dashboards: Dashboard[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreateOrgDashboard?: () => void;
  /** The name stays plain text, with its pill, instead of a greyed-out trigger. */
  isEditMode?: boolean;
}

const TITLE_CLASS = "truncate text-xl font-semibold tracking-tight";

type DashboardRowProps = { dashboard: Dashboard };

const DashboardRow: FC<DashboardRowProps> = ({ dashboard }) => {
  const identity = getDashboardIdentity(dashboard);
  return (
    <DropdownMenuRadioItem value={dashboard.id} className="pl-9">
      <span className="min-w-0 flex-1 truncate font-medium">
        {dashboard.name}
      </span>
      {dashboard.isDefault && (
        <span className="text-xs text-muted-foreground">Standard</span>
      )}
      {/* Released to sub-tenants by this tenant (LAR-93). */}
      {dashboard.isSharedWithSubTenants && !dashboard.isFromParentTenant && (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Share2 className="size-3" aria-hidden="true" />
          Delt
        </span>
      )}
      <DashboardIdentityPill identity={identity} size="row" />
      {identity.hasUnpublishedChanges && <UnpublishedChangesChip size="dot" />}
    </DropdownMenuRadioItem>
  );
};

type GroupLabelProps = { id: string; icon: ReactNode; children: ReactNode };

/**
 * Radix's menu groups, unlike the select's, do not name themselves after their label; the group
 * points at it with `aria-labelledby`, so a screen reader hears "Fra DAK" before the row.
 */
const GroupLabel: FC<GroupLabelProps> = ({ id, icon, children }) => (
  <DropdownMenuLabel
    id={id}
    className="flex items-center gap-2 text-[11px] font-semibold tracking-wide uppercase"
  >
    {icon}
    {children}
  </DropdownMenuLabel>
);

/**
 * The dashboard name is the dropdown (LAR-661): the header names the dashboard and says whose it
 * is, and switching shows the same pill on every row, grouped by audience.
 */
const DashboardSelector: FC<DashboardSelectorProps> = ({
  dashboards,
  selectedId,
  onSelect,
  onCreateOrgDashboard,
  isEditMode,
}) => {
  const groups = groupDashboardsByAudience(dashboards);
  const selectedDashboard = dashboards.find((d) => d.id === selectedId);
  const identity = selectedDashboard
    ? getDashboardIdentity(selectedDashboard)
    : null;

  const title = selectedDashboard?.name ?? "Vælg dashboard";
  const labelId = useId();
  const parentLabelId = `${labelId}-parent`;
  const organisationLabelId = `${labelId}-organisation`;
  const personalLabelId = `${labelId}-personal`;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
      {/* The negative margin sits here, not on the trigger: on the trigger it is subtracted from
          the h1's width while max-w-full still caps the trigger at it, truncating every name. */}
      <h1 className="-ml-2 min-w-0">
        {isEditMode ? (
          <span className={`block px-2 py-1 ${TITLE_CLASS}`}>{title}</span>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* No id here: it would replace the one Radix gives the trigger, which the open menu
                  names itself after. Radix also marks it as a menu button, so the name alone says
                  it switches dashboards and the heading reads as the dashboard's name. */}
              <Button
                variant="ghost"
                className="h-auto min-w-0 max-w-full gap-2 px-2 py-1"
              >
                <span className={TITLE_CLASS}>{title}</span>
                <ChevronDown
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-[380px] max-w-[calc(100vw-2rem)]"
            >
              <DropdownMenuRadioGroup
                value={selectedId ?? undefined}
                onValueChange={onSelect}
              >
                {groups.parent.length > 0 && (
                  <>
                    <DropdownMenuGroup aria-labelledby={parentLabelId}>
                      <GroupLabel
                        id={parentLabelId}
                        icon={
                          <Share2 className="size-3.5" aria-hidden="true" />
                        }
                      >
                        Fra {parentTenantDisplayName(groups.parentTenantName)}
                      </GroupLabel>
                      {groups.parent.map((dashboard) => (
                        <DashboardRow
                          key={dashboard.id}
                          dashboard={dashboard}
                        />
                      ))}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                  </>
                )}

                <DropdownMenuGroup aria-labelledby={organisationLabelId}>
                  <GroupLabel
                    id={organisationLabelId}
                    icon={<Building2 className="size-3.5" aria-hidden="true" />}
                  >
                    Organisation
                  </GroupLabel>
                  {/* Drafts are only listed for the administrators who can publish them (LAR-93). */}
                  {groups.organisation.map((dashboard) => (
                    <DashboardRow key={dashboard.id} dashboard={dashboard} />
                  ))}
                  {groups.organisation.length === 0 && (
                    <div className="py-2 pr-3 pl-9 text-sm text-muted-foreground">
                      Ingen organisationsdashboards
                    </div>
                  )}
                </DropdownMenuGroup>
              </DropdownMenuRadioGroup>

              <PermissionGate allOf={[Permission.UpdateDashboardLayout]}>
                <DropdownMenuItem
                  onSelect={() => onCreateOrgDashboard?.()}
                  className="pl-9 text-primary"
                  id="create-org-dashboard-action"
                >
                  <Plus aria-hidden="true" />
                  Opret organisationsdashboard
                </DropdownMenuItem>
              </PermissionGate>

              <DropdownMenuSeparator />

              <DropdownMenuRadioGroup
                value={selectedId ?? undefined}
                onValueChange={onSelect}
              >
                <DropdownMenuGroup aria-labelledby={personalLabelId}>
                  <GroupLabel
                    id={personalLabelId}
                    icon={<User className="size-3.5" aria-hidden="true" />}
                  >
                    Personligt
                  </GroupLabel>
                  {groups.personal.map((dashboard) => (
                    <DashboardRow key={dashboard.id} dashboard={dashboard} />
                  ))}
                  {groups.personal.length === 0 && (
                    <div className="py-2 pr-3 pl-9 text-sm text-muted-foreground">
                      Ingen personlige dashboards
                    </div>
                  )}
                </DropdownMenuGroup>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </h1>

      {identity && <DashboardIdentityPill identity={identity} />}
      {identity?.hasUnpublishedChanges && <UnpublishedChangesChip />}
    </div>
  );
};

export default DashboardSelector;
